import { type Consumer, createConsumer, Subscription } from "@rails/actioncable"
import { captureException } from "@sentry/react"
import { auth } from "auth"
import { useConfettiBannerStore } from "../components/ConfettiBanner/store"
import { BACKOFFICE_API_URL, IS_DEV_BUILD } from "../env"

const MAX_RETRY_ATTEMPTS = 5
const ATTEMPT_DELAY = [0, 5000, 10000, 20000, 30000]

type ActionCableState = {
  consumer: Consumer | null
  subscription: Subscription | null
}
type ReconnectionState = {
  restoreAttemptCount: number
  reconnectTimeoutId: NodeJS.Timeout | null
}

const defaultActionCableState: ActionCableState = {
  consumer: null,
  subscription: null,
}
const defaultReconnectionState: ReconnectionState = {
  restoreAttemptCount: 0,
  reconnectTimeoutId: null,
}

const actionCableState: ActionCableState = { ...defaultActionCableState }
const reconnectionState: ReconnectionState = {
  ...defaultReconnectionState,
}

/**
 * An action cable is a web socket connection to our Ruby back-end.
 * It's based on a library unique to Ruby and is intended to facilitate
 * real-time communication between a front-end and a Ruby back-end.
 *
 * ⚠️ OBS! This function will be called recursively if connection dies.
 */
export const initializeActionCable = async () => {
  const url = await auth.addAccessParams(`${BACKOFFICE_API_URL}/cable`)
  const consumer = createConsumer(url)

  if (consumer === null || consumer === undefined) {
    const errorMsg =
      "An Action Cable web socket instance does not exist. This should never happen."
    errorLog(errorMsg)
    return
  }

  actionCableState.consumer = consumer

  createConfettiSubscription()
}

/**
 * Currently the only subscription in the action cable is updating
 * a playthrough that has been processed and we use it for firing
 * confetti and updating the FE
 */
const createConfettiSubscription = () => {
  // This will never happen but TypeScript cries anyway
  if (!actionCableState.consumer) return

  const CHANNEL_NAME = "PlaythroughProcessedChannel"

  actionCableState.subscription =
    actionCableState.consumer.subscriptions.create(CHANNEL_NAME, {
      initialized: () => {
        debugLog(`[${CHANNEL_NAME}] Initialized`)
      },

      connected: () => {
        debugLog(`[${CHANNEL_NAME}] Connected`)
        clearState()
      },

      // "disconnected" is called on WebSocket connection closing.
      // This happens on:
      // - invalid/unauthorized request - ex. reconnecting with used transfer_token
      // - network drop - ex. when the user loses internet connection
      // - app getting set to background - ex. tapping away from SKILLS app
      //   or user's mobile device screen locking
      // - server restart
      //
      // Reconnection attempts will fail because transfer_token used to
      // create the consumer is one-time use, so we have to nuke the entire
      // consumer and attempt to recreate it with a fresh token.
      disconnected: () => {
        debugLog(`[${CHANNEL_NAME}] Disconnected`)
        retryInitializeActionCable()
      },

      // "rejected" is called on subscription rejection.
      rejected: () => {
        debugLog(`[${CHANNEL_NAME}] Rejected`)
        retryInitializeActionCable()
      },

      received: async () => {
        const { updateCurrentProductProgress } =
          useConfettiBannerStore.getState()
        updateCurrentProductProgress()
      },
    })
}

/**
 * This method nukes the action cable and creates it anew.
 * This is used when action cable cannot reconnect due to
 * transfer_key not being valid anymore after first use.
 */
const retryInitializeActionCable = () => {
  const shouldAbortRetry =
    reconnectionState.restoreAttemptCount >= MAX_RETRY_ATTEMPTS

  if (shouldAbortRetry) {
    // At this point user won't be able to receive live playthrough updates until a refresh.
    const errorMsg = "Maximum restoration attempts reached!"
    errorLog(errorMsg)
    return
  }

  const attemptDelay = ATTEMPT_DELAY[reconnectionState.restoreAttemptCount]

  clearReconnectTimeout()

  reconnectionState.reconnectTimeoutId = setTimeout(
    // Recursive function that reinitializes the action cable
    () => {
      reconnectionState.restoreAttemptCount += 1

      const currentAttempt = reconnectionState.restoreAttemptCount
      debugLog(
        `Restoring consumer - attempt ${currentAttempt}/${MAX_RETRY_ATTEMPTS}`
      )

      resetActionCableState()
      initializeActionCable()
    },
    attemptDelay
  )
}

const clearState = () => {
  clearReconnectTimeout()
  reconnectionState.restoreAttemptCount =
    defaultReconnectionState.restoreAttemptCount
}

const clearReconnectTimeout = () => {
  if (reconnectionState.reconnectTimeoutId) {
    clearTimeout(reconnectionState.reconnectTimeoutId)
  }
  reconnectionState.reconnectTimeoutId =
    defaultReconnectionState.reconnectTimeoutId
}

const resetActionCableState = () => {
  actionCableState.subscription?.unsubscribe()
  actionCableState.subscription = null

  actionCableState.consumer?.disconnect()
  actionCableState.consumer = null
}

const debugLog = (message: string, ...args: unknown[]) => {
  if (IS_DEV_BUILD) {
    console.log(`[ActionCable] ${message}`, ...args)
  }
}

const errorLog = (message: string, ...args: unknown[]) => {
  const messageWithActionCable = `[ActionCable] ${message}`
  console.error(messageWithActionCable, ...args)
  captureException(new Error(messageWithActionCable))
}
